import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Téléphone", value: "06 18 90 74 35", href: "tel:+33618907435", testid: "contact-info-phone" },
  { icon: Mail, label: "Email", value: "d.contactdimora@gmail.com", href: "mailto:d.contactdimora@gmail.com", testid: "contact-info-email" },
  { icon: MapPin, label: "Secteur", value: "Basée à Gap — 04 & 05", testid: "contact-info-address" },
  { icon: Clock, label: "Horaires", value: "Lun — Sam · 7h à 21h", testid: "contact-info-hours" },
];

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all duration-300 focus:border-amber-400/60 focus:bg-white/[0.07] focus:shadow-[0_0_20px_rgba(242,193,78,0.1)]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! DIMORA vous répond très vite.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Envoi impossible. Appelez-nous au 06 18 90 74 35.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 sm:py-36 bg-[#0D1220]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">Contact</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Parlons de <span className="text-amber-300">vos espaces</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
            {INFO.map((info, i) => (
              <motion.div
                key={info.label}
                data-testid={info.testid}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="rounded-2xl border border-white/10 bg-[#111723]/75 p-6 hover:border-amber-400/40 transition-colors duration-300"
              >
                <info.icon className="w-5 h-5 text-amber-300 mb-4" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1">{info.label}</p>
                {info.href ? (
                  <a href={info.href} className="text-sm font-semibold text-white hover:text-amber-300 transition-colors">
                    {info.value}
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-white">{info.value}</p>
                )}
              </motion.div>
            ))}
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl border border-white/10 bg-[#111723]/75 card-glow p-8 sm:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Votre nom" value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} />
              <input required type="email" placeholder="Votre email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} />
            </div>
            <input type="tel" placeholder="Téléphone (optionnel)" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className={inputCls} />
            <textarea
              required
              rows={5}
              placeholder="Décrivez votre besoin..."
              value={form.message}
              onChange={set("message")}
              data-testid="contact-message-input"
              className={`${inputCls} resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] hover:shadow-[0_0_35px_rgba(242,193,78,0.45)] disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Envoi..." : "Envoyer le message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
